import type { FC, ReactNode } from 'react'
import type { UrlObject } from 'url'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/lib/format'
import { buttonVariants } from '@/components/ui/button'
import { useQueryHref } from '@/hooks/use-query-href'
import { PAGE_SIZE } from '@/constants/dashboard'

interface PaginationProps {
    page: number
    pageCount: number
    total: number
    noun: string
}

interface PageLinkProps {
    href: UrlObject
    disabled: boolean
    label: string
    icon: LucideIcon
    iconSide: 'start' | 'end'
}

const PageLink: FC<PageLinkProps> = ({ href, disabled, label, icon: Icon, iconSide }): ReactNode => {
    const className = cn(buttonVariants({ variant: 'outline' }), 'h-9 gap-1 bg-card px-3')
    const content = (
        <>
            {iconSide === 'start' && <Icon aria-hidden='true' className='size-4' />}
            {label}
            {iconSide === 'end' && <Icon aria-hidden='true' className='size-4' />}
        </>
    )

    if (disabled) {
        return (
            <span aria-disabled='true' className={cn(className, 'pointer-events-none opacity-50')}>
                {content}
            </span>
        )
    }

    return (
        <Link href={href} className={className}>
            {content}
        </Link>
    )
}

export const Pagination: FC<PaginationProps> = ({ page, pageCount, total, noun }): ReactNode => {
    const hrefFor = useQueryHref()

    const first = total === 0 ? 0 : Math.min((page - 1) * PAGE_SIZE + 1, total)
    const last = Math.min(page * PAGE_SIZE, total)

    return (
        <nav
            aria-label='Pagination'
            className='flex flex-col items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm sm:flex-row'
        >
            <p className='text-muted-foreground'>
                Showing{' '}
                <span className='font-semibold text-foreground tabular-nums'>{formatNumber(first)}</span>
                {' '}to{' '}
                <span className='font-semibold text-foreground tabular-nums'>{formatNumber(last)}</span>
                {' '}of{' '}
                <span className='font-semibold text-foreground tabular-nums'>{formatNumber(total)}</span>
                {' '}{noun}
            </p>

            <div className='flex items-center gap-2'>
                <PageLink
                    href={hrefFor({ page: page - 1 > 1 ? page - 1 : null })}
                    disabled={page <= 1}
                    label='Previous'
                    icon={ChevronLeft}
                    iconSide='start'
                />

                <span className='px-1 text-muted-foreground tabular-nums'>
                    {page} / {pageCount}
                </span>

                <PageLink
                    href={hrefFor({ page: page + 1 })}
                    disabled={page >= pageCount}
                    label='Next'
                    icon={ChevronRight}
                    iconSide='end'
                />
            </div>
        </nav>
    )
}
